import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navbar, Nav, NavDropdown, Container, Offcanvas } from 'react-bootstrap';
import { useTheme } from 'app/providers/ThemeContext';
import './MyNavbar.css';

const THEME_OPTIONS = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

const NAVBAR_FIT_BUFFER = 4;

const PRIMARY_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/blog', label: 'Blog' },
  { href: '/contact-form', label: 'Contact' },
];

const NAV_DROPDOWNS = [
  {
    label: 'Ham Radio',
    items: [
      { href: '/ham-radio-learning', label: 'Ham Radio Learning' },
      { href: '/ham-radio-license-quiz', label: 'Ham Radio License Quiz' },
      { href: '/ham-radio-band-plan', label: 'Ham Radio Band Plan' },
      { href: '/morse-code-trainer', label: 'Morse Code Trainer' },
      { href: '/ham-radio-q-codes', label: 'Q Codes' },
      { href: '/Nato-phonetic-alphabet-trainer', label: 'Nato Alphabet' },
      { href: '/ham-radio-emulator', label: 'Ham Radio Emulator' },
    ],
  },
  {
    label: 'Meshtastic',
    icon: 'meshtastic',
    items: [{ href: '/meshtastic-weather-station', label: 'Meshtastic Weather Station' }],
  },
  {
    label: 'Software Stuff',
    items: [{ href: '/react-image-gallery', label: 'React Image Gallery' }],
  },
];

function getGapWidth(element) {
  if (!element) {
    return 0;
  }

  const styles = window.getComputedStyle(element);
  const gapValue = styles.columnGap && styles.columnGap !== 'normal' ? styles.columnGap : styles.gap;

  return Number.parseFloat(gapValue) || 0;
}

function MeshtasticNavIcon() {
  return (
    <svg
      className="site-navbar__meshtastic-icon"
      viewBox="0 0 64 48"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M9 34 L21 13"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="5.4"
      />
      <path
        d="M27 34 L39 13 L55 34"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="5.4"
      />
    </svg>
  );
}

function NavAdornment({ dropdown }) {
  if (dropdown.icon === 'meshtastic') {
    return <MeshtasticNavIcon />;
  }

  return null;
}

export default function MyNavbar() {
  const { themePreference, resolvedTheme, isDarkMode, setThemePreference } = useTheme();
  const containerRef = useRef(null);
  const measureContentRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [shouldCollapse, setShouldCollapse] = useState(true);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measureContent = measureContentRef.current;

    if (!container || !measureContent) {
      return undefined;
    }

    let frameId = 0;

    const updateLayoutMode = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const requiredWidth = getGapWidth(container) + measureContent.scrollWidth;
        const availableWidth = container.clientWidth;

        setShouldCollapse(requiredWidth > availableWidth - NAVBAR_FIT_BUFFER);
      });
    };

    updateLayoutMode();

    const resizeObserver = new ResizeObserver(updateLayoutMode);
    resizeObserver.observe(container);
    resizeObserver.observe(measureContent);

    window.addEventListener('resize', updateLayoutMode);
    window.visualViewport?.addEventListener('resize', updateLayoutMode);
    document.fonts?.ready?.then(updateLayoutMode).catch(() => {});

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateLayoutMode);
      window.visualViewport?.removeEventListener('resize', updateLayoutMode);
    };
  }, [themePreference]);

  useEffect(() => {
    if (!shouldCollapse && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [isMenuOpen, shouldCollapse]);

  return (
    <Navbar
      className="site-navbar"
      collapseOnSelect
      expand={!shouldCollapse}
      expanded={shouldCollapse ? isMenuOpen : false}
      onToggle={setIsMenuOpen}
      data-bs-theme={resolvedTheme}
    >
      <Container ref={containerRef} fluid className="site-navbar__container">
        <Navbar.Toggle className="site-navbar__toggle" aria-controls="site-navbar-offcanvas" />
        <Navbar.Offcanvas
          className="site-navbar__offcanvas"
          id="site-navbar-offcanvas"
          aria-labelledby="site-navbar-title"
          placement="end"
          data-bs-theme={resolvedTheme}
        >
          <Offcanvas.Header
            className="site-navbar__offcanvas-header"
            closeButton
            closeVariant={isDarkMode ? 'white' : undefined}
          >
            <Offcanvas.Title className="site-navbar__title" id="site-navbar-title">
              Hello, thanks for visiting
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="site-navbar__offcanvas-body">
            <Nav className="site-navbar__nav flex-grow-1 pe-3">
              {PRIMARY_LINKS.map((link) => (
                <Nav.Link key={link.href} className="site-navbar__link" href={link.href}>
                  {link.label}
                </Nav.Link>
              ))}
              {NAV_DROPDOWNS.map((dropdown) => (
                <NavDropdown
                  key={dropdown.label}
                  className="site-navbar__dropdown"
                  title={
                    <span className="site-navbar__dropdown-title">
                      <span>{dropdown.label}</span>
                      <NavAdornment dropdown={dropdown} />
                    </span>
                  }
                  menuVariant={isDarkMode ? 'dark' : undefined}
                >
                  {dropdown.items.map((item) => (
                    <NavDropdown.Item
                      key={item.href}
                      className="site-navbar__dropdown-item"
                      href={item.href}
                    >
                      {item.label}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              ))}
            </Nav>

            <div className="site-navbar__theme-controls">
              <span className="site-navbar__theme-label">Theme</span>
              <div className="site-navbar__theme-toggle" role="group" aria-label="Theme selection">
                {THEME_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`site-navbar__theme-button ${themePreference === option.value ? 'site-navbar__theme-button--active' : ''
                      }`}
                    onClick={() => setThemePreference(option.value)}
                    aria-pressed={themePreference === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </Offcanvas.Body>
        </Navbar.Offcanvas>
        <div className="site-navbar__measure" aria-hidden="true">
          <div ref={measureContentRef} className="site-navbar__measure-content">
            <div className="site-navbar__measure-nav">
              {PRIMARY_LINKS.map((link) => (
                <span key={link.href} className="site-navbar__link nav-link site-navbar__measure-item">
                  {link.label}
                </span>
              ))}
              {NAV_DROPDOWNS.map((dropdown) => (
                <span
                  key={dropdown.label}
                  className="site-navbar__measure-toggle nav-link dropdown-toggle"
                >
                  <span>{dropdown.label}</span>
                  <NavAdornment dropdown={dropdown} />
                </span>
              ))}
            </div>
            <div className="site-navbar__theme-controls site-navbar__measure-theme">
              <span className="site-navbar__theme-label">Theme</span>
              <div className="site-navbar__theme-toggle" role="presentation">
                {THEME_OPTIONS.map((option) => (
                  <span
                    key={option.value}
                    className={`site-navbar__theme-button ${themePreference === option.value ? 'site-navbar__theme-button--active' : ''
                      }`}
                  >
                    {option.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Navbar>
  );
}
